/**
 * ProviderRequestEntry — the homework cards that help an owner ORDER a site survey or as-built
 * drawings (docs/OWNER_PROVIDER_REQUESTS.md, Matt + Brandon 2026-11-02). One component, two entry
 * types: survey_request and asbuilt_request.
 *
 * The owner picks providers who serve the project's county (or adds their own), ticks the
 * acknowledgement, and the request goes out in their name; proposals come straight back to them.
 * Alder James never orders, manages or reviews these (agreement 1.3.x / 2.1.2). The server owns the
 * provider list, the acknowledgement's words and the email (api/_ownerProviderRequest.js); this
 * card only shows them and sends the picks.
 *
 * entry.providerRequest: { kind, ackText, max, facts, providers: [{id,name,blurb}], requests: [...] }
 */

import { useState } from 'react'
import Markdown from '../../components/Markdown.jsx'
import { J, PrimaryButton, GhostButton, DoneChip, ViewOnlyNote, fmtJourneyDate } from '../journeyUI.jsx'
import { sendProviderRequest } from '../../services/journey.js'

const COPY = {
  site_survey: {
    title: 'Order a site survey',
    why: 'Every design decision about setbacks, height and drainage starts from an accurate survey of your lot. The survey is yours to provide, prepared by a licensed land surveyor you hire.',
    listLabel: 'Surveyors we have worked with',
    one: 'surveyor', many: 'surveyors',
    have: 'I already have a survey',
    haveDone: 'Thank you. We will get a copy of your survey from you.',
    ordered: 'I already ordered one',
    orderedDone: 'Please forward the survey to us when it arrives.',
    byLabel: 'Who is preparing it?',
  },
  as_built: {
    title: 'Order as-built drawings of your home',
    why: 'Because your project works with the house that is there today, the design starts from accurate drawings of it as it stands: every room, wall, window and roof line, measured on site. These drawings are yours to provide, prepared by a measurement firm you hire.',
    listLabel: 'Firms we have worked with',
    one: 'firm', many: 'firms',
    have: 'I already have drawings of the house',
    haveDone: 'Thank you. We will get a copy of your drawings from you.',
    ordered: 'I already ordered them',
    orderedDone: 'Please forward the drawings to us when they arrive.',
    byLabel: 'Who is preparing them?',
  },
}

const ERRORS = {
  no_owner_email: 'We need your email address on file to send this in your name. Please ask your project team to add it.',
  ack_required: 'Please tick the acknowledgement first.',
  nobody_to_send: 'Pick at least one, or add your own.',
  too_many: 'That is more than this request can go to.',
  no_address: 'The project address is missing. Please ask your project team to add it.',
}

const S = {
  section: { fontFamily: '"Montserrat", sans-serif', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#3a5260', margin: '26px 0 10px' },
  pick: on => ({ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 16px', border: on ? '0.5px solid #07272D' : '0.5px solid rgba(7,39,45,0.14)', borderRadius: 8, background: on ? '#f8fafb' : '#fff', cursor: 'pointer', marginBottom: 8 }),
  name: { fontFamily: '"DM Sans", sans-serif', fontSize: 14, fontWeight: 600, color: '#07272D' },
  blurb: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#7a9aaa' },
  input: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans", sans-serif', fontSize: 14, padding: '9px 12px', border: '0.5px solid rgba(7,39,45,0.25)', borderRadius: 4, color: '#07272D', background: '#fff' },
  small: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, lineHeight: 1.6, color: '#3a5260' },
  facts: { display: 'grid', gridTemplateColumns: '110px 1fr', rowGap: 6, padding: '14px 16px', background: '#f8fafb', borderRadius: 8, fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#07272D' },
  err: { fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: '#c0392b', marginTop: 12 },
}

export default function ProviderRequestEntry({ entry, response, onRespond, viewOnly, token }) {
  const d = entry.providerRequest || {}
  const kind = d.kind || (entry.type === 'asbuilt_request' ? 'as_built' : 'site_survey')
  const C = COPY[kind]
  const providers = d.providers || []
  const facts = d.facts || {}
  const [requests, setRequests] = useState(d.requests || [])
  const sentIds = new Set(requests.map(r => r.providerId).filter(Boolean))
  const room = Math.max(0, (d.max || 4) - requests.length)

  const [picked, setPicked] = useState(() => providers.filter(p => !sentIds.has(p.id)).slice(0, Math.min(3, room)).map(p => p.id))
  const [own, setOwn] = useState([])          // [{ name, email }]
  const [ownName, setOwnName] = useState('')
  const [ownEmail, setOwnEmail] = useState('')
  const [titleReport, setTitleReport] = useState('')
  const [occupied, setOccupied] = useState('')
  const [access, setAccess] = useState('')
  const [ack, setAck] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(requests.length === 0)

  // "I already have one" / "I already ordered one": the card's homework_done answer (payload.status
  // 'have' | 'ordered', with who is preparing it and when it is expected for an order).
  const answered = response && response.kind === 'homework_done' ? (response.payload || {}) : null
  const have = !!answered
  const answeredOrdered = answered && answered.status === 'ordered'
  const [answering, setAnswering] = useState(null)   // null | 'ordered' (the small form before confirming)
  const [confirming, setConfirming] = useState(false) // the last look before real emails go out
  const [orderedBy, setOrderedBy] = useState('')
  const [expected, setExpected] = useState('')
  const count = picked.length + own.length

  function toggle(id) {
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : (p.length + own.length < room ? [...p, id] : p))
  }
  function addOwn() {
    const name = ownName.trim(), email = ownEmail.trim()
    if (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || count >= room) return
    setOwn(o => [...o, { name, email }]); setOwnName(''); setOwnEmail('')
  }
  // Sending emails real firms, so it takes a second, deliberate click (Matt, 2026-11-02).
  function askToSend() {
    setError(null)
    if (!ack) { setError(ERRORS.ack_required); return }
    if (!count) { setError(ERRORS.nobody_to_send); return }
    setConfirming(true)
  }
  const recipientsNamed = [...providers.filter(p => picked.includes(p.id)).map(p => p.name), ...own.map(o => o.name)]
  async function send() {
    setError(null)
    if (!ack) { setError(ERRORS.ack_required); return }
    if (!count) { setError(ERRORS.nobody_to_send); return }
    setConfirming(false)
    setBusy(true)
    try {
      const out = await sendProviderRequest(token, entry.id,
        { providerIds: picked, own, ack: true, titleReport: titleReport || undefined, occupied: occupied || undefined, access: access.trim() || undefined }, providers)
      setRequests(r => out.preview ? [...r, ...(out.requests || [])] : (out.requests || r))
      setPicked([]); setOwn([]); setAck(false); setAdding(false)
      if (out.failed && out.failed.length) setError(`It did not go through to ${out.failed.join(', ')}. Please contact them directly.`)
    } catch (e) {
      setError(ERRORS[e.code] || 'Something went wrong sending this. Please try again.')
    }
    setBusy(false)
  }

  const where = [facts.address, [facts.city, [facts.state, facts.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean).join(', ')

  return (
    <div>
      <div style={J.eyebrow}>Homework</div>
      <h2 style={J.title}>{entry.title || C.title}</h2>
      <hr style={J.rule} />
      <div style={J.body}>
        {entry.data && entry.data.intro ? <Markdown text={entry.data.intro} /> : <p style={{ margin: 0 }}>{C.why}</p>}
        {!have && <p style={{ margin: '14px 0 0' }}>
          Pick the {C.many} you would like proposals from. We send each of them the request, with your address and our list, in your name. Their proposals come straight to you. Choosing a {C.one} and agreeing their fee is between you and them.
        </p>}
      </div>

      {have && !answeredOrdered && <div style={{ marginTop: 28 }}><DoneChip>{C.have}</DoneChip><p style={{ ...S.small, marginTop: 10 }}>{C.haveDone}</p></div>}
      {answeredOrdered && (
        <div style={{ marginTop: 28 }}>
          <DoneChip>Already ordered</DoneChip>
          <p style={{ ...S.small, marginTop: 10 }}>
            {[answered.by ? 'From ' + answered.by : '', answered.expected ? 'expected ' + fmtJourneyDate(answered.expected) : ''].filter(Boolean).join(', ')}{answered.by || answered.expected ? '. ' : ''}{C.orderedDone}
          </p>
        </div>
      )}
      {/* A mis-click must be reversible (Matt, 2026-11-02). Sending is not, so it gets a confirm instead. */}
      {have && !viewOnly && (
        <button type="button" onClick={() => { setAnswering(null); onRespond('homework_undo', {}) }}
          style={{ background: 'none', border: 'none', padding: '8px 0', minHeight: 44, cursor: 'pointer', color: '#244c5a', textDecoration: 'underline', fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
          Change my answer
        </button>
      )}

      {requests.length > 0 && (
        <>
          <div style={S.section}>Sent</div>
          {requests.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, ...S.small }}>
              {r.status === 'failed'
                ? <span style={{ ...J.mono, color: '#c0392b' }}>Did not go through</span>
                : <span style={{ ...J.mono, color: '#1D9E75', background: '#eaf8f2', padding: '3px 10px', borderRadius: 20 }}>Sent {r.sentAt ? fmtJourneyDate(r.sentAt) : ''}</span>}
              <span style={{ flexGrow: 1 }}>{r.name}</span>
            </div>
          ))}
          <p style={{ ...S.small, marginTop: 6 }}>Their proposals will come to you by email. Reply to them directly.</p>
          {!viewOnly && room > 0 && !adding && <div style={{ marginTop: 12 }}><GhostButton onClick={() => setAdding(true)}>Send to another {C.one}</GhostButton></div>}
        </>
      )}

      {viewOnly && requests.length === 0 && !have && <div style={{ marginTop: 28 }}><ViewOnlyNote>The owner sends this request.</ViewOnlyNote></div>}

      {!viewOnly && !have && adding && room > 0 && (
        <>
          <div style={S.section}>{C.listLabel}{facts.county ? ` in ${facts.county} County` : ''}</div>
          {providers.filter(p => !sentIds.has(p.id)).map(p => (
            <label key={p.id} style={S.pick(picked.includes(p.id))}>
              <input type="checkbox" checked={picked.includes(p.id)} onChange={() => toggle(p.id)} style={{ width: 18, height: 18, accentColor: '#07272D', margin: 0 }} />
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}><span style={S.name}>{p.name}</span><span style={S.blurb}>{p.blurb}</span></span>
            </label>
          ))}
          {providers.length === 0 && <p style={S.small}>We do not have a {C.one} on file for this area yet. You can add one you know below.</p>}
          {own.map((o, i) => (
            <div key={i} style={{ ...S.pick(true), cursor: 'default' }}>
              <span style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}><span style={S.name}>{o.name}</span><span style={S.blurb}>Added by you · {o.email}</span></span>
              <button type="button" onClick={() => setOwn(x => x.filter((_, j) => j !== i))} aria-label={'Remove ' + o.name} style={{ background: 'none', border: 'none', color: '#7a9aaa', cursor: 'pointer', fontSize: 18, minWidth: 44, minHeight: 44 }}>×</button>
            </div>
          ))}
          {count < room && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
              <input aria-label={`Your ${C.one}'s name`} placeholder={`Add a ${C.one} of your own: name`} value={ownName} onChange={e => setOwnName(e.target.value)} style={{ ...S.input, flex: '2 1 180px', width: 'auto' }} />
              <input aria-label="Their email" placeholder="Email" type="email" value={ownEmail} onChange={e => setOwnEmail(e.target.value)} style={{ ...S.input, flex: '2 1 180px', width: 'auto' }} />
              <GhostButton onClick={addOwn} disabled={!ownName.trim() || !ownEmail.trim()}>Add</GhostButton>
            </div>
          )}

          <div style={S.section}>What goes out with it</div>
          <div style={S.facts}>
            <span style={{ color: '#7a9aaa' }}>Address</span><span>{where || 'Missing'}</span>
            {facts.apn && <><span style={{ color: '#7a9aaa' }}>Parcel</span><span style={{ fontFamily: '"DM Mono", monospace' }}>{facts.apn}</span></>}
            {facts.projectType && <><span style={{ color: '#7a9aaa' }}>Work planned</span><span>{facts.projectType}</span></>}
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            {kind === 'site_survey' ? (
              <label style={{ ...S.small, flex: '1 1 220px' }}>Title report
                <select value={titleReport} onChange={e => setTitleReport(e.target.value)} style={{ ...S.input, marginTop: 4 }}>
                  <option value="">Choose…</option>
                  <option value="have">I have a current one</option>
                  <option value="ordered">Ordered, will forward</option>
                  <option value="none">I do not have one yet</option>
                </select>
              </label>
            ) : (
              <label style={{ ...S.small, flex: '1 1 220px' }}>Is the house lived in?
                <select value={occupied} onChange={e => setOccupied(e.target.value)} style={{ ...S.input, marginTop: 4 }}>
                  <option value="">Choose…</option>
                  <option value="yes">Yes, we live here</option>
                  <option value="no">No, it is empty</option>
                </select>
              </label>
            )}
            <label style={{ ...S.small, flex: '2 1 260px' }}>Site access (optional)
              <input value={access} onChange={e => setAccess(e.target.value)} placeholder="e.g. Vacant lot, or call me to arrange" style={{ ...S.input, marginTop: 4 }} />
            </label>
          </div>
          {kind === 'site_survey' && titleReport === 'none' && <p style={{ ...S.small, marginTop: 8 }}>Without a current title report the survey may leave out recorded easements. Your title or escrow company can order one.</p>}
          <p style={{ ...S.small, marginTop: 8 }}>Please do not put gate codes or alarm codes here. The {C.one} will contact you.</p>

          <label style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginTop: 22, cursor: 'pointer' }}>
            <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} style={{ width: 18, height: 18, accentColor: '#07272D', margin: '2px 0 0', flexShrink: 0 }} />
            <span style={S.small}>{d.ackText}</span>
          </label>

          {error && <div style={S.err}>{error}</div>}
          {confirming ? (
            <div style={{ marginTop: 24, padding: '18px 20px', border: '0.5px solid rgba(7,39,45,0.22)', borderRadius: 10, background: '#f8fafb' }}>
              <div style={{ ...S.name, marginBottom: 8 }}>Send {count === 1 ? 'this request' : `${count} requests`} now?</div>
              <p style={{ ...S.small, margin: 0 }}>
                An email goes to {recipientsNamed.join(', ').replace(/, ([^,]*)$/, ' and $1')} in your name, with you copied.
                Their proposals will come to you. Once sent, it cannot be called back.
              </p>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 16 }}>
                <PrimaryButton onClick={send} disabled={busy}>{count === 1 ? 'Yes, send it' : `Yes, send all ${count}`}</PrimaryButton>
                <GhostButton onClick={() => setConfirming(false)} disabled={busy}>Go back</GhostButton>
              </div>
            </div>
          ) : (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginTop: 24 }}>
            <PrimaryButton onClick={askToSend} disabled={busy || !count || !ack}>
              {busy ? 'Sending…' : `Send the request to ${count || ''} ${count === 1 ? C.one : C.many}`.replace('  ', ' ')}
            </PrimaryButton>
            {requests.length === 0 && <GhostButton onClick={() => onRespond('homework_done', { have: true, status: 'have' })} disabled={busy}>{C.have}</GhostButton>}
            {requests.length === 0 && <GhostButton onClick={() => setAnswering(a => a === 'ordered' ? null : 'ordered')} disabled={busy}>{C.ordered}</GhostButton>}
            {requests.length > 0 && <GhostButton onClick={() => setAdding(false)} disabled={busy}>Cancel</GhostButton>}
          </div>
          )}
          {answering === 'ordered' && (
            <div style={{ marginTop: 18, padding: '16px 18px', border: '0.5px solid rgba(7,39,45,0.14)', borderRadius: 8, background: '#f8fafb' }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <label style={{ ...S.small, flex: '2 1 220px' }}>{C.byLabel} (optional)
                  <input value={orderedBy} onChange={e => setOrderedBy(e.target.value)} placeholder="Company or surveyor" style={{ ...S.input, marginTop: 4 }} />
                </label>
                <label style={{ ...S.small, flex: '1 1 160px' }}>Expected by (optional)
                  <input type="date" value={expected} onChange={e => setExpected(e.target.value)} style={{ ...S.input, marginTop: 4 }} />
                </label>
              </div>
              <div style={{ marginTop: 14 }}>
                <PrimaryButton onClick={() => onRespond('homework_done', { status: 'ordered', by: orderedBy.trim().slice(0, 120) || undefined, expected: expected || undefined })}>Let our team know</PrimaryButton>
              </div>
            </div>
          )}
        </>
      )}
      {!viewOnly && !have && room === 0 && requests.length > 0 && <p style={{ ...S.small, marginTop: 10 }}>This request has gone to as many {C.many} as it can.</p>}
      {error && !adding && <div style={S.err}>{error}</div>}

      {/* An open door (Matt, 2026-11-02): ordering a survey is new to most owners. Same firm contact
          the Hub's welcome screen shows (JourneyAnticipation.jsx). */}
      <hr style={{ ...J.rule, margin: '30px 0 16px' }} />
      <p style={{ ...S.small, margin: 0 }}>
        Questions, or something here you did not expect? We are glad to walk you through it. Call us at{' '}
        <a href="tel:+19495299070" style={{ color: '#244c5a', whiteSpace: 'nowrap' }}>949-529-9070</a> or email{' '}
        <a href="mailto:hello@alderjames.com" style={{ color: '#244c5a' }}>hello@alderjames.com</a>.
      </p>
    </div>
  )
}
